import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
  signal,
  computed,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { ApiService } from '../../../core/services/api.service';
import { ToastService } from '../../../core/services/toast.service';
import { S3ImageUploadService } from '../../../core/services/s3-image-upload.service';
import { API_ROUTES } from '../../../core/constants/api-routes';
import {
  ActiveCategoryResponseModel,
  PromptResponseModel,
  EmotionalToneResponseModel,
  FamilyMember,
  CreateAndPublishStoryRequest,
  StoryQuestionItem,
  UiPromptItem,
  promptIdToUuid,
  AddCustomQuestionRequest,
  UpdateCustomQuestionRequest,
} from '../../../core/models/story.models';

// ── Recording state machine ───────────────────────────────────────────────────
type RecordingState = 'idle' | 'requesting' | 'ready' | 'recording' | 'paused' | 'done';
type Step4Phase    = 'record' | 'organize';

@Component({
  selector: 'app-story-record',
  templateUrl: './story-record.html',
  styleUrl: './story-record.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
})
export class StoryRecord implements AfterViewInit, OnDestroy {
  private router      = inject(Router);
  private api         = inject(ApiService);
  private toast       = inject(ToastService);
  private s3          = inject(S3ImageUploadService);
  private cdr         = inject(ChangeDetectorRef);
  private platformId  = inject(PLATFORM_ID);

  // ── Template refs ──────────────────────────────────────────────────────────
  @ViewChild('webcamVideo')   webcamVideoEl!: ElementRef<HTMLVideoElement>;
  @ViewChild('previewVideo')  previewVideoEl!: ElementRef<HTMLVideoElement>;
  @ViewChild('videoFileInput') videoFileInputEl!: ElementRef<HTMLInputElement>;

  // ── Steps ─────────────────────────────────────────────────────────────────
  readonly currentStep  = signal<1 | 2 | 3 | 4>(1);
  readonly step4Phase   = signal<Step4Phase>('record');

  goToStep(step: 1 | 2 | 3 | 4): void { this.currentStep.set(step); }

  nextStep(): void {
    const s = this.currentStep();
    if (s < 4) this.currentStep.set((s + 1) as 1 | 2 | 3 | 4);
    else if (s === 4 && this.step4Phase() === 'record') this.step4Phase.set('organize');
  }

  prevStep(): void {
    const s = this.currentStep();
    if (s === 4 && this.step4Phase() === 'organize') { this.step4Phase.set('record'); return; }
    if (s > 1) this.currentStep.set((s - 1) as 1 | 2 | 3 | 4);
    else this.router.navigate(['/app/stories'], { replaceUrl: true });
  }

  cancelRecord(): void { this.router.navigate(['/app/stories'], { replaceUrl: true }); }

  // ── Step 1: Categories ────────────────────────────────────────────────────
  readonly categories        = signal<ActiveCategoryResponseModel[]>([]);
  readonly selectedCategoryId= signal<number | null>(null);
  readonly loadingCategories = signal(false);

  readonly selectedCategory = computed(() =>
    this.categories().find(c => c.categoryId === this.selectedCategoryId())
  );

  selectCategory(id: number): void { this.selectedCategoryId.set(id); }

  async loadCategories(): Promise<void> {
    if (this.categories().length) return;
    this.loadingCategories.set(true);
    const res = await this.api.request<ActiveCategoryResponseModel[]>(
      'GET', API_ROUTES.GET_ACTIVE_CATEGORIES, undefined, { showToaster: false }
    );
    this.loadingCategories.set(false);
    if (res.success && res.data) {
      this.categories.set(res.data);
      if (res.data.length) this.selectedCategoryId.set(res.data[0].categoryId);
    }
    this.cdr.markForCheck();
  }

  async onStep1Continue(): Promise<void> {
    if (!this.selectedCategoryId()) { this.toast.error('Please select a category.'); return; }
    await this.loadPromptsForCategory(this.selectedCategoryId()!);
    this.nextStep();
  }

  // ── Step 2: Media Type ─────────────────────────────────────────────────────
  /** 1=Video, 2=Audio, 3=Image+Audio */
  readonly selectedMediaType = signal<1 | 2 | 3>(1);

  readonly mediaTypeOptions = [
    { id: 1 as const, label: 'Video Recording',   description: 'Record or upload a video to share your story',     icon: 'images/icons/memory-share-icons/fm-ts-icon1.svg' },
    { id: 2 as const, label: 'Audio Recording',   description: 'Record or upload audio to tell your story',        icon: 'images/icons/memory-share-icons/fm-ts-icon2.svg' },
    { id: 3 as const, label: 'Image + Audio',     description: 'Upload a photo and describe it with audio',         icon: 'images/icons/memory-share-icons/fm-ts-icon3.svg' },
  ];

  selectMediaType(id: 1 | 2 | 3): void { this.selectedMediaType.set(id); }

  // ── Step 3: Questions ──────────────────────────────────────────────────────
  readonly prompts         = signal<UiPromptItem[]>([]);
  readonly loadingPrompts  = signal(false);
  readonly addingQuestion  = signal(false);
  readonly newQuestionDraft = signal('');
  readonly savingQuestion  = signal(false);

  readonly selectedPrompts = computed(() => this.prompts().filter(p => p.selected));

  async loadPromptsForCategory(categoryId: number): Promise<void> {
    this.loadingPrompts.set(true);
    const res = await this.api.request<PromptResponseModel[]>(
      'GET', `${API_ROUTES.GET_PROMPTS_FOR_CATEGORY}/${categoryId}`, undefined, { showToaster: false }
    );
    this.loadingPrompts.set(false);
    if (res.success && res.data) {
      this.prompts.set(res.data.map(p => this.toUiPromptItem(p)));
    }
    this.cdr.markForCheck();
  }

  private toUiPromptItem(p: PromptResponseModel): UiPromptItem {
    const isCustom = p.source?.toLowerCase() === 'custom' || !!p.customQuestionId;
    const key = isCustom ? `custom-${p.customQuestionId}` : `ai-${p.promptMasterId}`;
    return {
      key,
      promptMasterId: p.promptMasterId,
      customQuestionId: p.customQuestionId,
      questionText: p.promptText ?? '',
      source: isCustom ? 'Custom' : 'AI',
      selected: true,
      editing: false,
      editDraft: '',
    };
  }

  togglePrompt(key: string): void {
    this.prompts.update(list => list.map(p => p.key === key ? { ...p, selected: !p.selected } : p));
  }

  startEdit(key: string): void {
    this.prompts.update(list =>
      list.map(p => p.key === key ? { ...p, editing: true, editDraft: p.questionText } : p)
    );
  }

  cancelEdit(key: string): void {
    this.prompts.update(list => list.map(p => p.key === key ? { ...p, editing: false, editDraft: '' } : p));
  }

  updateEditDraft(key: string, value: string): void {
    this.prompts.update(list => list.map(p => p.key === key ? { ...p, editDraft: value } : p));
  }

  async saveEdit(item: UiPromptItem): Promise<void> {
    const text = item.editDraft.trim();
    if (!text || !item.customQuestionId) return;
    const body: UpdateCustomQuestionRequest = { customQuestionId: item.customQuestionId, questionText: text };
    const res = await this.api.request<boolean>('PUT', API_ROUTES.UPDATE_CUSTOM_QUESTION, body);
    if (res.success) {
      this.prompts.update(list =>
        list.map(p => p.key === item.key ? { ...p, questionText: text, editing: false, editDraft: '' } : p)
      );
    }
    this.cdr.markForCheck();
  }

  async deleteCustomQuestion(item: UiPromptItem): Promise<void> {
    if (!item.customQuestionId) return;
    const res = await this.api.request<boolean>(
      'DELETE', `${API_ROUTES.DELETE_CUSTOM_QUESTION}/${item.customQuestionId}`
    );
    if (res.success) {
      this.prompts.update(list => list.filter(p => p.key !== item.key));
    }
    this.cdr.markForCheck();
  }

  async saveNewQuestion(): Promise<void> {
    const text = this.newQuestionDraft().trim();
    if (!text) return;
    const catId = this.selectedCategoryId();
    if (!catId) return;
    this.savingQuestion.set(true);
    const body: AddCustomQuestionRequest = { categoryId: catId, questionText: text };
    const res = await this.api.request<string>('POST', API_ROUTES.ADD_CUSTOM_QUESTION, body);
    this.savingQuestion.set(false);
    if (res.success && res.data) {
      const newItem: UiPromptItem = {
        key: `custom-${res.data}`,
        promptMasterId: 0,
        customQuestionId: res.data,
        questionText: text,
        source: 'Custom',
        selected: true,
        editing: false,
        editDraft: '',
      };
      this.prompts.update(list => [...list, newItem]);
      this.newQuestionDraft.set('');
      this.addingQuestion.set(false);
    }
    this.cdr.markForCheck();
  }

  // ── Step 4 – Recording phase ───────────────────────────────────────────────
  readonly recordingState  = signal<RecordingState>('idle');
  readonly recordingSeconds= signal(0);
  readonly previewUrl      = signal<string | null>(null);
  readonly uploadedFileName= signal<string | null>(null);    // S3 key after upload
  readonly videoDuration   = signal(0);   // seconds of recorded/uploaded video
  readonly isUploading     = signal(false);

  private mediaStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private timerInterval: ReturnType<typeof setInterval> | null = null;

  ngAfterViewInit(): void { /* refs available */ }

  // — Webcam recording ——————————————————————————————————————————————————————

  async startCameraAndRecord(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    this.recordingState.set('requesting');
    this.cdr.markForCheck();
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      // Attach stream to live preview
      this.recordingState.set('recording');
      this.cdr.detectChanges();
      const vid = this.webcamVideoEl?.nativeElement;
      if (vid) { vid.srcObject = this.mediaStream; vid.play(); }
      // Start MediaRecorder immediately
      this.recordedChunks = [];
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : 'video/webm';
      this.mediaRecorder = new MediaRecorder(this.mediaStream, { mimeType });
      this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) this.recordedChunks.push(e.data);
      };
      this.mediaRecorder.onstop = () => this.onRecordingStopped();
      this.mediaRecorder.start(1000);
      this.recordingSeconds.set(0);
      this.timerInterval = setInterval(() => {
        this.recordingSeconds.update(s => s + 1);
        this.cdr.markForCheck();
      }, 1000);
    } catch {
      this.recordingState.set('idle');
      this.toast.error('Camera access denied. Please allow camera permissions and try again.');
    }
    this.cdr.markForCheck();
  }

  stopRecording(): void {
    this.mediaRecorder?.stop();
    this.stopTimer();
    this.stopCameraStream();
  }

  private onRecordingStopped(): void {
    const blob = new Blob(this.recordedChunks, { type: 'video/webm' });
    const url  = URL.createObjectURL(blob);
    this.previewUrl.set(url);
    this.videoDuration.set(this.recordingSeconds());
    this.trimEnd.set(this.recordingSeconds());
    this.recordingState.set('done');
    this.cdr.markForCheck();
    // Kick off S3 upload in background
    const file = new File([blob], `recording-${Date.now()}.webm`, { type: 'video/webm' });
    this.uploadMediaFile(file);
  }

  // — File upload ————————————————————————————————————————————————————————————

  triggerFileUpload(): void {
    this.videoFileInputEl?.nativeElement.click();
  }

  onVideoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0];
    if (!file) return;
    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    if (!validTypes.includes(file.type) && !file.type.startsWith('video/')) {
      this.toast.error('Please select a valid video file (MP4, WebM, MOV, AVI).');
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      this.toast.error('Video file must be under 500 MB.');
      return;
    }
    const url = URL.createObjectURL(file);
    this.previewUrl.set(url);
    this.recordingState.set('done');
    this.cdr.markForCheck();
    // Get duration
    const vid = document.createElement('video');
    vid.preload = 'metadata';
    vid.onloadedmetadata = () => {
      const dur = Math.round(vid.duration);
      this.videoDuration.set(dur);
      this.trimEnd.set(dur);
      this.cdr.markForCheck();
      URL.revokeObjectURL(vid.src);
    };
    vid.src = url;
    this.uploadMediaFile(file);
  }

  private async uploadMediaFile(file: File): Promise<void> {
    if (!this.s3.isConfigured()) {
      this.toast.error('Media upload is not configured. Please contact support.');
      return;
    }
    this.isUploading.set(true);
    this.cdr.markForCheck();
    try {
      const result = await this.s3.upload(file, 'storyMedia');
      this.uploadedFileName.set(result.key);
    } catch {
      this.toast.error('Failed to upload media. Please try again.');
    } finally {
      this.isUploading.set(false);
      this.cdr.markForCheck();
    }
  }

  retakeVideo(): void {
    // Stop any active recording/stream first
    this.mediaRecorder?.stop();
    this.stopTimer();
    this.stopCameraStream();
    if (this.previewUrl()) { URL.revokeObjectURL(this.previewUrl()!); this.previewUrl.set(null); }
    this.uploadedFileName.set(null);
    this.recordingSeconds.set(0);
    this.videoDuration.set(0);
    this.trimStart.set(0);
    this.trimEnd.set(0);
    this.recordingState.set('idle');
    this.cdr.markForCheck();
    // Automatically open camera and start recording
    this.startCameraAndRecord();
  }

  onContinueFromRecord(): void {
    if (!this.uploadedFileName() && this.recordingState() !== 'done') {
      this.toast.error('Please record or upload a video first.');
      return;
    }
    if (this.isUploading()) {
      this.toast.error('Please wait for the upload to finish.');
      return;
    }
    this.loadOrganizeData();
    this.step4Phase.set('organize');
  }

  // ── Step 4 – Organize phase ────────────────────────────────────────────────
  readonly storyTitle            = signal('');
  readonly storyDate             = signal('');
  readonly storyLocation         = signal('');
  readonly selectedEmotionalToneId = signal<number | null>(null);
  /** 1=Private, 2=Shared, 3=Public, 5=SelectedMembers */
  readonly visibilityTypeId      = signal<number>(2);
  readonly taggedMemberIds       = signal<string[]>([]);
  readonly sharedWithUserIds     = signal<string[]>([]);
  readonly transcriptVerbatim    = signal('');
  readonly transcriptNarrative   = signal('');
  /** 1=Verbatim, 2=Narrative */
  readonly selectedTranscriptType = signal<number>(1);
  readonly trimStart             = signal(0);
  readonly trimEnd               = signal(0);

  readonly emotionalTones        = signal<EmotionalToneResponseModel[]>([]);
  readonly familyMembers         = signal<FamilyMember[]>([]);
  readonly loadingOrganize       = signal(false);
  readonly isSubmitting          = signal(false);

  private organizeDataLoaded = false;

  async loadOrganizeData(): Promise<void> {
    if (this.organizeDataLoaded) return;
    this.loadingOrganize.set(true);
    const [tonesRes, membersRes] = await Promise.all([
      this.api.request<EmotionalToneResponseModel[]>('GET', API_ROUTES.GET_EMOTIONAL_TONES, undefined, { showToaster: false }),
      this.api.request<FamilyMember[]>('GET', API_ROUTES.GET_FAMILY_MEMBER_LIST, undefined, { showToaster: false }),
    ]);
    if (tonesRes.success && tonesRes.data)   this.emotionalTones.set(tonesRes.data);
    if (membersRes.success && membersRes.data) this.familyMembers.set(membersRes.data);
    this.loadingOrganize.set(false);
    this.organizeDataLoaded = true;
    this.cdr.markForCheck();
  }

  toggleTagMember(id: string): void {
    this.taggedMemberIds.update(ids =>
      ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]
    );
  }

  toggleShareMember(id: string): void {
    this.sharedWithUserIds.update(ids =>
      ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]
    );
  }

  onTrimStartChange(val: number): void {
    const end = this.trimEnd();
    this.trimStart.set(Math.min(val, end - 1));
  }

  onTrimEndChange(val: number): void {
    const start = this.trimStart();
    this.trimEnd.set(Math.max(val, start + 1));
  }

  formatSeconds(s: number): string {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  }

  async publishStory(): Promise<void> {
    const title = this.storyTitle().trim();
    if (!title) { this.toast.error('Story title is required.'); return; }
    const catId = this.selectedCategoryId();
    if (!catId) { this.toast.error('No category selected.'); return; }

    const questions: StoryQuestionItem[] = this.selectedPrompts().map((p, i) => ({
      questionId: p.source === 'Custom' && p.customQuestionId
        ? p.customQuestionId
        : promptIdToUuid(p.promptMasterId),
      questionSource: p.source === 'Custom' ? 2 : 1,
      questionOrder: i + 1,
    }));

    const payload: CreateAndPublishStoryRequest = {
      categoryId:             catId,
      mediaType:              this.selectedMediaType(),
      questions:              questions.length ? questions : undefined,
      title,
      mediaFileName:          this.uploadedFileName() ?? null,
      duration:               this.videoDuration() || null,
      storyDate:              this.storyDate() || null,
      location:               this.storyLocation() || null,
      emotionalToneId:        this.selectedEmotionalToneId(),
      visibilityTypeId:       this.visibilityTypeId(),
      transcriptVerbatim:     this.transcriptVerbatim() || null,
      transcriptNarrative:    this.transcriptNarrative() || null,
      selectedTranscriptType: this.selectedTranscriptType(),
      trimStart:              this.trimStart() > 0 ? this.trimStart() : null,
      trimEnd:                this.trimEnd() < this.videoDuration() ? this.trimEnd() : null,
      taggedFamilyMemberIds:  this.taggedMemberIds().length ? this.taggedMemberIds() : null,
      sharedWithUserIds:      this.sharedWithUserIds().length ? this.sharedWithUserIds() : null,
    };

    this.isSubmitting.set(true);
    const res = await this.api.request<{ storyId: string; message: string }>('POST', API_ROUTES.CREATE_AND_PUBLISH_STORY, payload);
    this.isSubmitting.set(false);
    if (res.success) {
      this.toast.success(res.data?.message ?? 'Story published successfully!');
      this.router.navigate(['/app/stories'], { replaceUrl: true });
    }
    this.cdr.markForCheck();
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private stopTimer(): void {
    if (this.timerInterval) { clearInterval(this.timerInterval); this.timerInterval = null; }
  }

  private stopCameraStream(): void {
    this.mediaStream?.getTracks().forEach(t => t.stop());
    this.mediaStream = null;
  }

  ngOnDestroy(): void {
    this.stopTimer();
    this.stopCameraStream();
    if (this.previewUrl()) URL.revokeObjectURL(this.previewUrl()!);
  }

  // Init
  ngOnInit(): void {
    this.loadCategories();
  }
}

